import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { beforeAfterEntries, safeEntries } from './types';

export function BeforeAfterPanel({
  after,
  before,
  title,
}: {
  readonly after: unknown;
  readonly before: unknown;
  readonly title: string;
}) {
  const { t } = useTranslation();
  const entries = beforeAfterEntries(before, after);
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" sx={{ mb: 2 }} variant="h5">
        {title}
      </Typography>
      {entries.length === 0 ? (
        <Typography color="text.secondary">{t('audit.noChangesRecorded')}</Typography>
      ) : (
        <Stack spacing={2}>
          {entries.map((entry) => (
            <Box key={entry.key}>
              <Typography component="h3" variant="subtitle2">
                {entry.key}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 0.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography color="text.secondary" component="span" variant="caption">
                    {t('audit.fields.before')}
                  </Typography>
                  <Typography sx={{ wordBreak: 'break-word' }}>
                    {entry.before ?? t('common.notAvailable')}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography color="text.secondary" component="span" variant="caption">
                    {t('audit.fields.after')}
                  </Typography>
                  <Typography sx={{ wordBreak: 'break-word' }}>
                    {entry.after ?? t('common.notAvailable')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export function SafeEntriesPanel({
  record,
  title,
}: {
  readonly record: unknown;
  readonly title: string;
}) {
  const { t } = useTranslation();
  const entries = safeEntries(record);
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" sx={{ mb: 2 }} variant="h5">
        {title}
      </Typography>
      {entries.length === 0 ? (
        <Typography color="text.secondary">{t('audit.noAdditionalFields')}</Typography>
      ) : (
        <Stack spacing={1}>
          {entries.map((entry) => (
            <Stack direction="row" key={entry.key} spacing={1}>
              <Typography sx={{ fontWeight: 600 }}>{entry.key}:</Typography>
              <Typography sx={{ wordBreak: 'break-word' }}>{entry.value}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
